import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@apollo/client'
import { ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { GET_DAILY_STATS, GET_TIME_SLOT_STATS } from '../../graphql/reports-queries'
import { formatEuros, dateRangeToUtc } from '../../lib/format'
import type { DayStat } from '../../types/order'
import { salesCents, avgOrderCents } from '../../components/financials/salesBasis'
import { usePermissions } from '../../hooks/usePermissions'
import { PERMISSIONS } from '../../lib/permissions'
import {
  AccessDenied,
  DAY_LABELS,
  EmptyData,
  MonthBar,
  QueryError,
  StatCard,
  TableLoading,
  currentLocalMonth,
  isoDayOf,
  monthLabel,
  parseYmd,
} from './shared'
import FinancialOverviewTab from './FinancialOverviewTab'

// ─── Margin tone ──────────────────────────────────────────────────────────────

// Owner-set thresholds (2026-08-29, re-aimed at MARGIN 2026-08-30): daily product
// margin (contribution margin), not sales — the owner reads days by what was made
// on the food. Delivery fees are sales but never in it (owner, 2026-09-30).
const TONE_THRESHOLDS = { great: 70000, good: 60000, fair: 50000, weak: 40000 }

/** Full literal Tailwind classes — the JIT compiler cannot see interpolated ones. */
const marginTone = (cents: number): string => {
  if (cents >= TONE_THRESHOLDS.great) return 'bg-emerald-600 text-white hover:bg-emerald-700'
  if (cents >= TONE_THRESHOLDS.good) return 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
  if (cents >= TONE_THRESHOLDS.fair) return 'bg-amber-100 text-amber-800 hover:bg-amber-200'
  if (cents >= TONE_THRESHOLDS.weak) return 'bg-red-100 text-red-700 hover:bg-red-200'
  return 'bg-red-600 text-white hover:bg-red-700'
}

const LEGEND: { swatch: string; label: string }[] = [
  { swatch: 'bg-emerald-600', label: '≥ €700' },
  { swatch: 'bg-emerald-100', label: '≥ €600' },
  { swatch: 'bg-amber-100', label: '≥ €500' },
  { swatch: 'bg-red-100', label: '≥ €400' },
  { swatch: 'bg-red-600', label: '< €400' },
]

// ─── Day cell ─────────────────────────────────────────────────────────────────

const DayCell = ({
  day,
  stat,
  isFuture,
  selected,
  onClick,
}: {
  day: number
  stat: DayStat | undefined
  isFuture: boolean
  selected: boolean
  onClick: () => void
}) => {
  const base =
    'relative min-h-[4.5rem] rounded-lg p-1.5 flex flex-col items-start justify-between text-left transition-colors w-full'

  if (isFuture) {
    return (
      <button type="button" disabled className={`${base} bg-white text-gray-200`}>
        <span className="text-[11px] font-medium">{day}</span>
      </button>
    )
  }

  // A closed day gets no threshold colour — a Monday the shop is shut is not a
  // catastrophe, and painting it red would read as one.
  if (!stat) {
    return (
      <button type="button" disabled className={`${base} bg-gray-50`}>
        <span className="text-[11px] font-medium text-gray-300">{day}</span>
      </button>
    )
  }

  const incompleteCogs = (stat.linesWithoutCogsCount ?? 0) > 0
  const margin = stat.contributionMarginCents
  const title =
    `Product margin ${margin != null ? formatEuros(margin) : '—'} · revenue ${formatEuros(
      salesCents(stat, false)
    )} · ${stat.orderCount} orders` + (incompleteCogs ? ' — COGS incomplete, margin overstated' : '')

  // Defensive: margin can only be null without order.financial.view, which the
  // page gate excludes — but a partial response must not crash or mis-color.
  const tone = margin != null ? marginTone(margin) : 'bg-gray-100 text-gray-500'

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`${base} ${tone}${selected ? ' ring-2 ring-gray-900 ring-inset' : ''}`}
    >
      <span className="text-[11px] font-medium opacity-70">{day}</span>
      {incompleteCogs && (
        <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-amber-400" />
      )}
      <span className="text-sm font-bold">{margin != null ? `€${Math.round(margin / 100)}` : '—'}</span>
      <span className="text-[11px] opacity-70">{stat.orderCount}×</span>
    </button>
  )
}

// ─── Daypart breakdown (mounted only while a day is expanded → lazy fetch) ─────

interface DaypartCell {
  iso_day: number
  daypart: string | null
  daypart_label: string | null
  order_count: number
  revenue_cents: number | null
  avg_order_value_cents: number | null
}

interface DaypartDef {
  key: string
  label: string
}

const DayDaypartBreakdown = ({ date }: { date: string }) => {
  // Over a one-day range the weekday rollup IS that day, so by_daypart filtered
  // on the date's iso_day gives this day's dayparts — no per-day API field needed.
  // The average order here means the same as the day's (product sales ÷ orders,
  // ex VAT) but timeSlotStats derives it from the order header, orderStats from
  // the lines: they agree to rounding, not to the cent.
  const { data, loading, error } = useQuery<{
    timeSlotStats: { by_daypart: DaypartCell[]; dayparts: DaypartDef[] }
  }>(GET_TIME_SLOT_STATS, {
    variables: { filters: dateRangeToUtc(date, date) },
    fetchPolicy: 'cache-and-network',
  })

  const stats = data?.timeSlotStats

  const rows = useMemo(() => {
    if (!stats) return []
    const isoDay = isoDayOf(parseYmd(date))
    const cells = stats.by_daypart.filter((c) => c.iso_day === isoDay && c.order_count > 0)
    // Ordered by the API's daypart order, not by whatever the cells arrive in.
    return stats.dayparts
      .map((dp) => cells.find((c) => c.daypart === dp.key))
      .filter((c): c is DaypartCell => c != null)
  }, [stats, date])

  if (loading && !stats) return <TableLoading />
  if (error) return <QueryError message={`Failed to load daypart breakdown: ${error.message}`} />
  if (rows.length === 0) return <p className="text-sm text-gray-400">No daypart data.</p>

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-xs text-gray-500 uppercase tracking-wide">
            <th className="text-left font-medium pb-2" />
            <th className="text-right font-medium pb-2">Orders</th>
            <th className="text-right font-medium pb-2">Avg order (ex. VAT)</th>
            <th className="text-right font-medium pb-2">Revenue</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {rows.map((c) => (
            <tr key={c.daypart ?? c.daypart_label}>
              {/* Labels come from the API — never derived from the key. */}
              <td className="py-2 text-gray-700">{c.daypart_label}</td>
              <td className="py-2 text-right text-gray-700">{c.order_count}</td>
              <td className="py-2 text-right text-gray-700">
                {c.avg_order_value_cents != null ? formatEuros(c.avg_order_value_cents) : '—'}
              </td>
              <td className="py-2 text-right font-medium text-gray-900">
                {c.revenue_cents != null ? formatEuros(c.revenue_cents) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const DailyReportsPage = () => {
  const { hasPermission } = usePermissions()
  const [month, setMonth] = useState(currentLocalMonth)
  const [selectedDay, setSelectedDay] = useState<string | null>(null)
  const [viewingFullDay, setViewingFullDay] = useState(false)
  const expansionRef = useRef<HTMLDivElement | null>(null)

  // Both permissions are required: orderStats silently floors the lower list
  // tiers to the last 7 days, so a page over an arbitrary month needs
  // order.list.all to get the numbers it claims to show.
  const canView =
    hasPermission(PERMISSIONS.ORDER_FINANCIAL_VIEW) && hasPermission(PERMISSIONS.ORDER_LIST_ALL)

  const [year, monthNo] = month.split('-').map(Number)
  const daysInMonth = new Date(year, monthNo, 0).getDate()
  const monthStart = `${month}-01`
  const monthEnd = `${month}-${String(daysInMonth).padStart(2, '0')}`

  // No clamping to today: stable variables for the whole month mean one cache
  // entry per month, and future days are simply absent server-side.
  const { data, loading, error } = useQuery<{ orderStats: { byDay: DayStat[] } }>(GET_DAILY_STATS, {
    variables: { filters: dateRangeToUtc(monthStart, monthEnd) },
    fetchPolicy: 'cache-and-network',
    skip: !canView,
  })

  const { dayMap, totalRevenue, totalOrders, totalCM, totalProductSales, tradingDays } = useMemo(() => {
    const map = new Map<string, DayStat>()
    for (const d of data?.orderStats?.byDay ?? []) map.set(d.date, d)

    // Totals are summed over the map, never over raw byDay — the map is what the
    // calendar renders, so the two can never disagree.
    let revenue = 0
    let orders = 0
    let productSales = 0
    let cm = 0
    let sawCm = false
    for (const d of map.values()) {
      // Revenue = everything sold, incl. VAT: goods plus both fees.
      revenue += salesCents(d, false)
      orders += d.orderCount
      // The margin is a PRODUCT margin, so its % is on product sales.
      productSales += d.productSalesExVatCents ?? 0
      if (d.contributionMarginCents != null) {
        cm += d.contributionMarginCents
        sawCm = true
      }
    }

    return {
      dayMap: map,
      totalRevenue: revenue,
      totalOrders: orders,
      totalCM: sawCm ? cm : null,
      totalProductSales: productSales,
      tradingDays: map.size,
    }
  }, [data])

  const selectedStat = selectedDay ? dayMap.get(selectedDay) : undefined

  useEffect(() => {
    if (selectedDay && expansionRef.current) {
      expansionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [selectedDay])

  if (!canView) return <AccessDenied />
  if (loading && !data) return <TableLoading />
  if (error) return <QueryError message={`Failed to load daily report: ${error.message}`} />

  const handleMonthChange = (next: string) => {
    setMonth(next)
    setSelectedDay(null)
    setViewingFullDay(false)
  }

  // ── Full-day drill ──
  if (viewingFullDay && selectedDay) {
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={() => setViewingFullDay(false)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Back to {monthLabel(month)}
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {parseYmd(selectedDay).toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </h1>
        <FinancialOverviewTab startDate={selectedDay} endDate={selectedDay} />
      </div>
    )
  }

  // ── Month view ──
  const nowLocal = new Date()
  const isCurrentMonth = month === currentLocalMonth()
  const todayDate = nowLocal.getDate()
  const leadingBlanks = ((new Date(year, monthNo - 1, 1).getDay() + 6) % 7) + 1 - 1

  const avgMargin =
    totalProductSales > 0 && totalCM != null ? `${((totalCM / totalProductSales) * 100).toFixed(1)}%` : '—'

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Daily Reports</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Per-day product margin — click a day for its daypart breakdown
        </p>
      </div>

      <MonthBar month={month} onChange={handleMonthChange} />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Revenue" value={formatEuros(totalRevenue)} />
        <StatCard label="Orders" value={String(totalOrders)} />
        <StatCard label="Product Margin" value={totalCM != null ? formatEuros(totalCM) : '—'} />
        {/* Pooled margin: total margin over total product sales. Never the mean of the
            per-day percentages — that would weight a quiet Tuesday like a busy Saturday. */}
        <StatCard label="Avg Margin" value={avgMargin} />
        <StatCard
          label="Avg / Trading Day"
          value={tradingDays > 0 ? formatEuros(Math.round(totalRevenue / tradingDays)) : '—'}
          sub={`${tradingDays} trading days`}
        />
      </div>

      {dayMap.size === 0 ? (
        <EmptyData />
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="overflow-x-auto">
            <div className="min-w-[640px]">
              <div className="grid grid-cols-7 gap-1 mb-1">
                {DAY_LABELS.map((label) => (
                  <div key={label} className="text-xs font-medium text-gray-400 text-center">
                    {label}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: leadingBlanks }, (_, i) => (
                  <div key={`blank-${i}`} />
                ))}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1
                  const date = `${month}-${String(day).padStart(2, '0')}`
                  return (
                    <DayCell
                      key={date}
                      day={day}
                      stat={dayMap.get(date)}
                      isFuture={isCurrentMonth && day > todayDate}
                      selected={selectedDay === date}
                      onClick={() => setSelectedDay(date === selectedDay ? null : date)}
                    />
                  )
                })}
              </div>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-[11px] text-gray-400">
                <span className="font-medium text-gray-500">Product margin:</span>
                {LEGEND.map((l) => (
                  <span key={l.label} className="inline-flex items-center gap-1">
                    <span className={`w-2.5 h-2.5 rounded-sm ${l.swatch}`} />
                    {l.label}
                  </span>
                ))}
                <span className="inline-flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm bg-gray-50 border border-gray-200" />
                  gray = closed
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedDay && selectedStat && (
        <div ref={expansionRef} className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
          <div className="flex items-start justify-between">
            <h2 className="text-base font-semibold text-gray-900">
              {parseYmd(selectedDay).toLocaleDateString('en-GB', {
                weekday: 'short',
                day: 'numeric',
                month: 'long',
              })}{' '}
              ·{' '}
              {selectedStat.contributionMarginCents != null
                ? `${formatEuros(selectedStat.contributionMarginCents)} product margin`
                : '—'}{' '}
              · {selectedStat.orderCount} orders
            </h2>
            <button
              type="button"
              onClick={() => setSelectedDay(null)}
              className="text-gray-400 hover:text-gray-600"
              title="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <DayDaypartBreakdown date={selectedDay} />

          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm border-t border-gray-100 pt-3">
            <span className="text-gray-600">
              Revenue{' '}
              <span className="font-medium text-gray-900">
                {formatEuros(salesCents(selectedStat, false))}
              </span>
            </span>
            <span className="text-gray-600">
              Avg order (ex. VAT){' '}
              <span className="font-medium text-gray-900">
                {selectedStat.orderCount > 0 ? formatEuros(avgOrderCents(selectedStat, true)) : '—'}
              </span>
            </span>
            {/* Gross: the API has no ex-VAT discount figure. Per day since API 2026-10-08. */}
            <span className="text-gray-600">
              Discounts applied (incl. VAT){' '}
              <span className="font-medium text-gray-900">
                {selectedStat.discountCents > 0 ? formatEuros(selectedStat.discountCents) : '—'}
              </span>
            </span>
            {/* Sales, but never in the product margin next to it. */}
            {(selectedStat.deliveryFeeExVatCents ?? 0) > 0 && (
              <span className="text-gray-600">
                Delivery fees (ex. VAT){' '}
                <span className="font-medium text-gray-900">
                  {formatEuros(selectedStat.deliveryFeeExVatCents ?? 0)}
                </span>
              </span>
            )}
            <span className="text-gray-600">
              Product margin{' '}
              <span
                className={`font-medium ${
                  (selectedStat.contributionMarginCents ?? 0) < 0 ? 'text-red-600' : 'text-emerald-700'
                }`}
              >
                {selectedStat.contributionMarginCents != null
                  ? formatEuros(selectedStat.contributionMarginCents)
                  : '—'}
              </span>
            </span>
            <span className="text-gray-600">
              Margin{' '}
              <span className="font-medium text-gray-900">
                {selectedStat.contributionMarginPct != null
                  ? `${selectedStat.contributionMarginPct.toFixed(1)}%`
                  : '—'}
              </span>
            </span>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setViewingFullDay(true)}
              className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
            >
              View full day →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default DailyReportsPage
